/**
 * Password validation utilities
 */

// Pentest 9.1 / 9.2: Güçlü parola politikası — backend ile birebir aynı kural.
// Min 8 karakter + en az 1 büyük harf, 1 küçük harf, 1 rakam ve 1 özel karakter.
export const MIN_PASSWORD_LENGTH = 8
const STRONG_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/

/**
 * Tek tek parola kuralları — UI'da canlı checklist göstermek için.
 * `labelKey` i18n anahtarıdır, `met` o anki parolanın kuralı sağlayıp sağlamadığını belirtir.
 */
export interface PasswordRequirement {
  key: string
  labelKey: string
  met: boolean
}

export const getPasswordRequirements = (password: string): PasswordRequirement[] => [
  {
    key: "length",
    labelKey: "passwordRules.length",
    met: password.length >= MIN_PASSWORD_LENGTH,
  },
  {
    key: "lowercase",
    labelKey: "passwordRules.lowercase",
    met: /[a-z]/.test(password),
  },
  {
    key: "uppercase",
    labelKey: "passwordRules.uppercase",
    met: /[A-Z]/.test(password),
  },
  {
    key: "digit",
    labelKey: "passwordRules.digit",
    met: /\d/.test(password),
  },
  {
    key: "special",
    labelKey: "passwordRules.special",
    met: /[^A-Za-z0-9]/.test(password),
  },
]

/**
 * Validates password meets minimum requirements
 * @param password - Password to validate
 * @returns Object with isValid flag and error message if invalid
 */
export const validatePassword = (password: string): { isValid: boolean; error?: string } => {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return {
      isValid: false,
      error: "login.passwordTooShort",
    }
  }
  if (!STRONG_PASSWORD_REGEX.test(password)) {
    return {
      isValid: false,
      error: "login.passwordTooWeak",
    }
  }
  return { isValid: true }
}

/**
 * Validates password confirmation matches password
 * @param password - Original password
 * @param confirmPassword - Confirmation password
 * @returns Object with isValid flag and error message if invalid
 */
export const validatePasswordConfirmation = (
  password: string,
  confirmPassword: string
): { isValid: boolean; error?: string } => {
  if (password !== confirmPassword) {
    return {
      isValid: false,
      error: "login.passwordsDoNotMatch",
    }
  }
  return { isValid: true }
}

/**
 * Validates password and confirmation together
 * @param password - Password to validate
 * @param confirmPassword - Confirmation password
 * @returns Object with isValid flag and first error message if invalid
 */
export const validatePasswordForm = (
  password: string,
  confirmPassword: string
): { isValid: boolean; error?: string } => {
  const passwordValidation = validatePassword(password)
  if (!passwordValidation.isValid) {
    return passwordValidation
  }

  const confirmationValidation = validatePasswordConfirmation(password, confirmPassword)
  if (!confirmationValidation.isValid) {
    return confirmationValidation
  }

  return { isValid: true }
}

